// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  Users, Plus, Loader2, Crown, Shield, UserCog, Eye, UserX, Sparkles,
  Bot, ChevronDown, Check, Lock,
} from 'lucide-react';
import { agentsApi } from '../../services/agentsApi';
import { RosterSummaryCard, type RosterSummary } from '../dossier/RosterSummaryCard';
import { PaywallModal } from '../billing/PaywallCard';
import { extractPaywall } from '../billing/paywall';
import { Tooltip } from '../ui/tooltip';
import { TierPill } from '../billing/TierPill';

// A workspace role is just a string from the backend — the catalog of roles
// (value + label) is served by the API (`assignable_roles`). The frontend never
// keeps its own list of which roles exist.
type Role = string;

interface RoleOption {
  value: string;
  label: string;
}

interface Member {
  id: string;
  email: string;
  name: string | null;
  role: string;
  is_founder?: boolean;
  /** Backend-computed: this row is the platform super-user (team@brndsh.io). */
  is_platform_team?: boolean;
  /** Backend-computed: this row is the consultant linked via client.consultant_id. */
  is_consultant?: boolean;
  /** Backend-computed: the caller may change/remove this member (strictly
   *  outranks them in the role hierarchy). Absent on older backends → fall
   *  back to the coarse `can_manage` flag. */
  can_manage_member?: boolean;
  /** Department placement (roster department_id → name), if any. */
  department_id?: string | null;
  department?: string | null;
  /** Provider lane only: this member is a flagged EXPERT (HITL engineer). */
  is_expert?: boolean;
  /** Provider lane only: this member (an owner) is a flagged builder ADMIN. */
  is_builder_admin?: boolean;
  /** Invited and has not accepted the invite yet. */
  invite_pending?: boolean;
}

interface MemberUsage {
  member_email: string | null;
  member_name: string | null;
  user_id: string | null;
  kind: 'member' | 'company_automation' | 'unattributed_delivery';
  calls: number;
  total_tokens: number;
  usage_value_usd: number;
  incurred_cost_usd: number;
  customer_charge_usd: number;
  last_used_at: string | null;
  breakdown: Array<{
    surface: string;
    calls: number;
    total_tokens: number;
    usage_value_usd: number;
    incurred_cost_usd: number;
    customer_charge_usd: number;
  }>;
}

interface Props {
  clientId: string;
  clientName: string;
}

type UsagePeriod = 'today' | 'last_7_days' | 'last_30_days' | 'all_time';

const usagePeriodLabel: Record<UsagePeriod, string> = {
  today: 'Today',
  last_7_days: 'Last 7 days',
  last_30_days: 'Last 30 days',
  all_time: 'All time',
};

function usageWindow(period: UsagePeriod): { since: string; until: string } {
  const now = new Date();
  if (period === 'today') {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    return { since: start.toISOString(), until: now.toISOString() };
  }
  if (period === 'last_7_days') {
    return { since: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(), until: now.toISOString() };
  }
  if (period === 'last_30_days') {
    return { since: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString(), until: now.toISOString() };
  }
  return { since: new Date(0).toISOString(), until: now.toISOString() };
}

function usageWindowDescription(period: UsagePeriod): string {
  const window = usageWindow(period);
  const start = new Date(window.since);
  const end = new Date(window.until);
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'local time';
  if (period === 'all_time') return `All recorded usage through ${end.toLocaleString()} (${zone})`;
  if (period === 'today') return `${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} to ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} (${zone})`;
  return `${start.toLocaleDateString()} to ${end.toLocaleDateString()} (${zone})`;
}

// Defensive only: used if the API response predates `assignable_roles` (e.g. a
// frontend deployed ahead of the backend). The backend catalog is the source of
// truth and overrides this whenever present.
const FALLBACK_ROLES: RoleOption[] = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'AI_PARTICIPANT', label: 'Employee' },
  { value: 'VIEWER', label: 'Viewer' },
];

// Presentation only: role value → icon + color. The frontend owns how a role
// LOOKS, not which roles exist. Anything unlisted falls back to a neutral style,
// so a new backend role still renders sensibly without a frontend change.
function roleMeta(role: string): { icon: any; className: string } {
  switch ((role || '').toUpperCase()) {
    case 'OWNER':
      return { icon: Crown, className: 'text-ink bg-canvas-inset ring-line' };
    case 'ADMIN':
      return { icon: Shield, className: 'text-ink bg-canvas-inset ring-line' };
    case 'AI_CHAMPION':
      return { icon: Sparkles, className: 'text-ink bg-canvas-inset ring-line' };
    case 'DEPARTMENT_HEAD':
      return { icon: Shield, className: 'text-ink bg-canvas-inset ring-line' };
    case 'AI_COACH':
      return { icon: UserCog, className: 'text-ink bg-canvas-inset ring-line' };
    case 'CONSULTANT':
      return { icon: UserCog, className: 'text-ink bg-card ring-line' };
    case 'EXPERT':
      return { icon: Shield, className: 'text-ink bg-card ring-line' };
    case 'VIEWER':
      return { icon: Eye, className: 'text-ink-dim bg-canvas ring-line' };
    default:
      return { icon: Users, className: 'text-ink bg-canvas ring-line' };
  }
}

// Columns of the people table, keyed by which optional columns show:
// b = builder access (provider owners), c = usage and cost.
const GRID: Record<string, string> = {
  '': 'md:grid-cols-[minmax(14rem,2fr)_minmax(9rem,1fr)_minmax(9rem,1fr)_2.5rem]',
  b: 'md:grid-cols-[minmax(14rem,2fr)_minmax(9rem,1fr)_minmax(9rem,1fr)_minmax(10.5rem,1fr)_2.5rem]',
  c: 'md:grid-cols-[minmax(14rem,2fr)_minmax(9rem,1fr)_minmax(9rem,1fr)_minmax(9rem,1.1fr)_5rem_2.5rem]',
  bc: 'md:grid-cols-[minmax(11rem,1.5fr)_minmax(9.75rem,1fr)_minmax(8.5rem,1fr)_minmax(9.5rem,1fr)_minmax(7.5rem,1fr)_4.25rem_2rem]',
};

// A labelled on/off pill for the provider lane's builder access.
const AccessToggle: React.FC<{ label: string; checked: boolean; disabled?: boolean; onChange: (v: boolean) => void }> = ({ label, checked, disabled, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-[12px] font-medium ring-1 ring-inset transition-colors disabled:opacity-50 ${checked ? ACCESS_ON : 'bg-white text-ink-soft ring-line hover:text-ink hover:ring-line-strong'}`}
  >
    {checked ? <Check className="h-3 w-3 text-ok-dim" strokeWidth={3} /> : <Plus className="h-3 w-3" />}
    {label}
  </button>
);

const ACCESS_ON = 'bg-ok-soft text-ink ring-[rgba(93,161,74,0.35)]';

// Builder admin is not switchable: an owner on a provider is always one.
const AdminBadge: React.FC = () => (
  <Tooltip content="Owners are always builder admins">
    <span className={`inline-flex h-7 cursor-default items-center gap-1.5 rounded-full px-2.5 text-[12px] font-medium ring-1 ring-inset ${ACCESS_ON}`}>
      <Shield className="h-3 w-3 text-ok-dim" />
      Admin
    </span>
  </Tooltip>
);

// Label fallback for a role not in the catalog: AI_CHAMPION → "Ai Champion".
// Only used when the backend catalog lacks the value; the catalog wins.
function prettifyRole(value: string): string {
  const words = (value || '').toLowerCase().split('_').filter(Boolean);
  return words.map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || 'Member';
}

const ClientTeamTab: React.FC<Props> = ({ clientId, clientName }) => {
  const [members, setMembers] = useState<Member[]>([]);
  const [canInvite, setCanInvite] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const [canViewMemberCosts, setCanViewMemberCosts] = useState(false);
  const [usageByEmail, setUsageByEmail] = useState<Record<string, MemberUsage>>({});
  const [unattributedUsage, setUnattributedUsage] = useState<MemberUsage[]>([]);
  const [usageComplete, setUsageComplete] = useState(true);
  const [usageLoading, setUsageLoading] = useState(false);
  const [openUsage, setOpenUsage] = useState<string | null>(null);
  // Whether the workspace being VIEWED is the provider, from the server. The
  // expert and builder-admin toggles are provider standing, so a provider person
  // looking at a customer's team must not see them.
  const [isProvider, setIsProvider] = useState(false);
  const [canSetExpert, setCanSetExpert] = useState(false);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [paywall, setPaywall] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('AI_PARTICIPANT');
  const [inviteDept, setInviteDept] = useState('');
  const [roleOptions, setRoleOptions] = useState<RoleOption[]>(FALLBACK_ROLES);
  const [departments, setDepartments] = useState<Array<{ id: string; name: string }>>([]);
  const [inviteBusy, setInviteBusy] = useState(false);
  // Invite-context feature (super-admin flag) + the context inputs it reveals.
  const [featureInviteContext, setFeatureInviteContext] = useState(false);
  const [inviteLevel, setInviteLevel] = useState('');
  const [inviteTitle, setInviteTitle] = useState('');
  const [inviteJobDesc, setInviteJobDesc] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  // A8 — held after an upload so the summary card can render above the roster.
  const [rosterSummary, setRosterSummary] = useState<RosterSummary | null>(null);
  const bulkInputRef = React.useRef<HTMLInputElement>(null);
  const loadedRef = React.useRef(false);
  const [mutatingEmail, setMutatingEmail] = useState<string | null>(null);
  const [usagePeriod, setUsagePeriod] = useState<UsagePeriod>('today');
  // Workspace tier read from /api/workspace/me so the consultant row's
  // tooltip CTA copy + the seat-usage pill stay in sync with the sidebar.
  const [planTier, setPlanTier] = useState<string | null>(null);

  const schedulingTiers = ['solo', 'team', 'scale', 'custom',
    'professional', 'business', 'enterprise'];
  const consultantTooltip = planTier && schedulingTiers.includes(planTier)
    ? 'Schedule a session with your solution partner.'
    : 'Async escalation only on Free. Upgrade to schedule a session.';

  // Human label for a role value, from the backend catalog (prettified fallback).
  const roleLabel = (value: string): string => {
    const v = (value || '').toUpperCase();
    return roleOptions.find(o => o.value.toUpperCase() === v)?.label || prettifyRole(v);
  };

  const load = useCallback(async () => {
    try {
      if (!loadedRef.current) setLoading(true);
      const { data } = await agentsApi.getWorkspace(clientId);
      setMembers((data?.members || []) as Member[]);
      setCanInvite(!!data?.can_invite);
      setCanManage(!!data?.can_manage);
      const maySeeCosts = !!data?.can_view_member_costs;
      setCanViewMemberCosts(maySeeCosts);
      setIsProvider(!!data?.is_provider);
      setCanSetExpert(!!data?.can_set_expert);
      // Role catalog comes from the backend; only fall back if it's absent.
      const roles = (data?.assignable_roles as RoleOption[]) || [];
      setRoleOptions(roles.length ? roles : FALLBACK_ROLES);
      // Workspace.me carries tier in Phase 4 — fall back to free if missing.
      setPlanTier((data?.plan_tier as string) || 'free');
      // Internal provider costs belong only on the provider company's own Team
      // page. Customer Team pages never request that ledger or its actors.
      if (data?.is_provider && maySeeCosts) {
        setUsageLoading(true);
        void agentsApi.getWorkspaceMemberUsage(clientId, usageWindow(usagePeriod)).then(usage => {
        const byEmail: Record<string, MemberUsage> = {};
        const unattributed: MemberUsage[] = [];
        for (const row of usage.data?.members || []) {
          if (row.kind === 'unattributed_delivery') {
            unattributed.push(row);
            continue;
          }
          const key = (row.member_email || '__company_automation__').toLowerCase();
          byEmail[key] = row;
        }
        setUsageByEmail(byEmail);
        setUnattributedUsage(unattributed);
        setUsageComplete(usage.data?.complete !== false);
      }).catch(() => {
        setUsageByEmail({});
        setUnattributedUsage([]);
        setUsageComplete(false);
      }).finally(() => {
        setUsageLoading(false);
      });
      } else {
        setUsageByEmail({});
        setUnattributedUsage([]);
        setUsageComplete(true);
        setUsageLoading(false);
      }
      // Departments for placement pickers — non-fatal, the tab works without.
      try {
        const dres = await agentsApi.listWorkspaceDepartments(clientId);
        setDepartments((dres.data?.departments || []).filter((d: any) => !d.archived));
      } catch { setDepartments([]); }
    } catch (err: any) {
      console.error('[ClientTeamTab] load failed', err);
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
      loadedRef.current = true;
    }
  }, [clientId, usagePeriod]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    agentsApi.getWorkspaceFeatures()
      .then(r => setFeatureInviteContext(Boolean(r.data?.invite_context)))
      .catch(() => setFeatureInviteContext(false));
  }, []);

  const buildInviteContext = (): Record<string, unknown> | null => {
    if (!featureInviteContext) return null;
    const ctx: Record<string, unknown> = {};
    if (inviteLevel) ctx.level = inviteLevel;
    if (inviteTitle.trim()) ctx.title = inviteTitle.trim();
    return Object.keys(ctx).length ? ctx : null;
  };

  // The raw CSV goes straight to the server, which detects the email/name/role/
  // department columns (whatever they're named), creates departments as needed,
  // and turns every other column into onboarding-prefill context. So any HR
  // export works — no fixed column names, no client-side parsing.
  const handleBulkFile = async (file: File) => {
    setBulkBusy(true);
    try {
      const text = await file.text();
      if (!text.trim()) { toast.error('That file looks empty'); return; }
      const { data } = await agentsApi.uploadWorkspaceMembersCsv(clientId, text);
      const failed = data.results.filter(r => r.status === 'failed' || r.status === 'seat_limit');
      const skippedNote = data.skipped.length ? `, ${data.skipped.length} skipped (no email)` : '';
      const failNote = failed.length ? ` — ${failed.length} needs attention` : '';
      toast.success(`Invited ${data.invited} of ${data.rows}${skippedNote}${failNote}`);
      if (failed.length || data.skipped.length) {
        console.warn('[ClientTeamTab] CSV rows needing attention', { failed, skipped: data.skipped });
      }
      // A8 — the roster is also a statement about the company. Show it back
      // instead of only counting invitations.
      if (data.summary) setRosterSummary(data.summary);
      setInviteOpen(false);
      await load();
    } catch (err: any) {
      console.error('[ClientTeamTab] bulk invite failed', err);
      toast.error(err?.response?.data?.detail || 'Bulk invite failed — check the file format');
    } finally {
      setBulkBusy(false);
      if (bulkInputRef.current) bulkInputRef.current.value = '';
    }
  };

  const handleInvite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    setInviteBusy(true);
    setPaywall(null);
    try {
      const context = buildInviteContext();
      const { data } = await agentsApi.addWorkspaceMember(clientId, {
        email,
        name: inviteName.trim() || undefined,
        role: inviteRole,
        department_id: inviteDept || undefined,
        // ROL-3: job title + description always seed the brief — not gated on
        // the invite-context flag (that flag only gates onboarding prefill).
        ...(inviteTitle.trim() ? { job_title: inviteTitle.trim() } : {}),
        ...(inviteJobDesc.trim() ? { job_description: inviteJobDesc.trim() } : {}),
        ...(context ? { context } : {}),
      });
      if (data?.created) {
        // No credentials ever travel by email (P2.3): the invitee sets their
        // own password from the accept link, and the copy must say so.
        toast.success(
          data?.email_sent
            ? `Invite sent to ${email}. They set their own password from the link.`
            : `${email} added (invite email failed to send; check SMTP)`
        );
      } else {
        toast.success(`${email} added as ${roleLabel(inviteRole).toLowerCase()}`);
      }
      if (data && data.builder_mirrored === false) {
        // The mirror is what lets this person open builder projects; a silent
        // failure here is exactly how "Members 0" happened in the builder.
        toast.warn(`${email} is on the team here, but could not be mirrored to the builder workspace yet. Projects will not see them until the sync succeeds; mention this to support if it persists.`);
      }
      setInviteOpen(false);
      setInviteEmail('');
      setInviteName('');
      setInviteRole('AI_PARTICIPANT');
      setInviteDept('');
      setInviteLevel('');
      setInviteTitle('');
      await load();
    } catch (err: any) {
      const pw = extractPaywall(err);
      if (pw) {
        // Seat quota gate — surface the paywall card with upgrade + add-seat CTAs.
        setPaywall(pw);
      } else {
        console.error('[ClientTeamTab] invite failed', err);
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === 'string' ? detail : 'Invite failed');
      }
    } finally {
      setInviteBusy(false);
    }
  };

  // After a successful seat top-up, retry the original invite.
  const handlePaywallTopUpSuccess = () => {
    setPaywall(null);
    if (inviteEmail.trim()) {
      // Defer to next tick so the closure picks up cleared paywall state.
      setTimeout(() => handleInvite(), 0);
    }
  };

  const handleRoleChange = async (email: string, role: string) => {
    setMutatingEmail(email);
    try {
      await agentsApi.updateWorkspaceMemberRole(clientId, email, role);
      toast.success(`Role updated for ${email}`);
      await load();
    } catch (err: any) {
      console.error('[ClientTeamTab] role change failed', err);
      toast.error(err?.response?.data?.detail || 'Role change failed');
    } finally {
      setMutatingEmail(null);
    }
  };

  const handleDeptChange = async (email: string, departmentId: string) => {
    setMutatingEmail(email);
    try {
      await agentsApi.setWorkspaceMemberDepartment(clientId, email, departmentId);
      toast.success(departmentId ? `Department updated for ${email}` : `Department cleared for ${email}`);
      await load();
    } catch (err: any) {
      console.error('[ClientTeamTab] department change failed', err);
      toast.error(err?.response?.data?.detail || 'Department change failed');
    } finally {
      setMutatingEmail(null);
    }
  };

  // Provider lane: flag/unflag a member as an expert (HITL engineer). Expert is
  // a flag, not a role — so this never changes their role, just grants them the
  // Builder link + build-lens on the crossing.
  const handleExpertToggle = async (email: string, isExpert: boolean) => {
    setMutatingEmail(email);
    try {
      await agentsApi.setWorkspaceMemberExpert(clientId, email, isExpert);
      toast.success(isExpert ? `${email} is now an expert` : `${email} is no longer an expert`);
      await load();
    } catch (err: any) {
      console.error('[ClientTeamTab] expert toggle failed', err);
      toast.error(err?.response?.data?.detail || 'Could not update expert');
    } finally {
      setMutatingEmail(null);
    }
  };

  const handleRemove = async (email: string) => {
    if (!window.confirm(`Remove ${email} from ${clientName}?`)) return;
    setMutatingEmail(email);
    try {
      await agentsApi.removeWorkspaceMember(clientId, email);
      toast.success(`${email} removed`);
      await load();
    } catch (err: any) {
      console.error('[ClientTeamTab] remove failed', err);
      toast.error(err?.response?.data?.detail || 'Remove failed');
    } finally {
      setMutatingEmail(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex items-center gap-3 text-ink-soft">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Loading team…</span>
        </div>
      </div>
    );
  }

  const compactNumber = (value: number): string => new Intl.NumberFormat('en-US', {
    notation: value >= 1000 ? 'compact' : 'standard',
    maximumFractionDigits: 1,
  }).format(value || 0);
  const dollars = (value: number): string => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
  const incurredCost = (usage?: { incurred_cost_usd: number } | null): number => Number(usage?.incurred_cost_usd || 0);
  const customerCharge = (usage?: { customer_charge_usd: number } | null): number => Number(usage?.customer_charge_usd || 0);
  const showBuilder = isProvider && canManage;
  const usageGrid = GRID[`${showBuilder ? 'b' : ''}${canViewMemberCosts ? 'c' : ''}`];
  const headCell = 'text-[10.5px] font-semibold uppercase tracking-[0.1em] text-ink-dim';
  const mobileLabel = 'w-24 shrink-0 text-[11px] font-medium uppercase tracking-[0.08em] text-ink-soft';
  const companyMembers = members.filter(member => !member.is_consultant && !member.is_platform_team);
  const maxMemberTokens = companyMembers.reduce((max, member) => Math.max(max, Number(usageByEmail[(member.email || '').toLowerCase()]?.total_tokens || 0)), 0);
  const servicePeople = members.filter(member => member.is_consultant && !member.is_platform_team);
  const allUsage = [
    ...Object.values(usageByEmail),
    ...unattributedUsage,
  ];
  const totalTokens = allUsage.reduce((sum, usage) => sum + Number(usage?.total_tokens || 0), 0);
  const totalCost = allUsage.reduce((sum, usage) => sum + incurredCost(usage), 0);
  const totalCharge = allUsage.reduce((sum, usage) => sum + customerCharge(usage), 0);

  return (
    <div>
      {/* A8 — what the upload told us about the company, above the roster it
          just created. Dismissable: it is a moment, not a permanent panel. */}
      {rosterSummary && (
        <div className="mb-5">
          <RosterSummaryCard
            summary={rosterSummary}
            onDismiss={() => setRosterSummary(null)}
          />
        </div>
      )}
      <section className="mb-5" aria-labelledby="people-heading">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 id="people-heading" className="m-0 text-2xl font-semibold tracking-[-0.02em] text-ink">People</h1>
              {/* Plan pill hidden for now.
              {planTier ? <TierPill tier={planTier as any} clickable /> : null}
              */}
            </div>
          </div>
          {canInvite || canViewMemberCosts ? (
            <div className="flex flex-wrap items-center gap-2">
              {canViewMemberCosts ? (
                <label className="sr-only" htmlFor="people-usage-period">Usage period</label>
              ) : null}
              {canViewMemberCosts ? (
                <select
                  id="people-usage-period"
                  value={usagePeriod}
                  onChange={event => setUsagePeriod(event.target.value as UsagePeriod)}
                  className="h-10 rounded-act border border-line bg-white px-3 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                >
                  <option value="today">Today</option>
                  <option value="last_7_days">Last 7 days</option>
                  <option value="last_30_days">Last 30 days</option>
                  <option value="all_time">All time</option>
                </select>
              ) : null}
              {canInvite && featureInviteContext ? (
                <>
                  <input
                    ref={bulkInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    onChange={event => { const file = event.target.files?.[0]; if (file) handleBulkFile(file); }}
                  />
                  <button
                    type="button"
                    onClick={() => bulkInputRef.current?.click()}
                    disabled={bulkBusy}
                    className="inline-flex h-10 items-center gap-2 rounded-act border border-line bg-white px-3.5 text-sm font-medium text-ink transition-colors hover:bg-canvas-inset focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-50 whitespace-nowrap"
                  >
                    {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    Import CSV
                  </button>
                </>
              ) : null}
              {canInvite ? (
                <button
                  type="button"
                  onClick={() => setInviteOpen(true)}
                  className="inline-flex h-10 items-center gap-2 rounded-act bg-ink px-4 text-sm font-semibold text-white transition-colors hover:bg-ink-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 whitespace-nowrap"
                >
                  <Plus className="h-4 w-4" />
                  Invite person
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        {isProvider && canViewMemberCosts ? (
          <div className="mt-5 grid overflow-hidden rounded-panel border border-line bg-white shadow-[0_1px_2px_rgba(24,25,29,0.04)] sm:grid-cols-3">
            {[
              ['Provider people', String(companyMembers.length)],
              [`AI usage · ${usagePeriodLabel[usagePeriod].toLowerCase()}`, usageLoading ? '…' : compactNumber(totalTokens)],
              [`Provider cost · ${usagePeriodLabel[usagePeriod].toLowerCase()}`, usageLoading ? '…' : dollars(totalCost)],
            ].map(([label, value], index) => (
              <div key={label} className={`px-5 py-4 ${index ? 'border-t border-line sm:border-l sm:border-t-0' : ''}`}>
                <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink-dim">{label}</div>
                <div className="mt-1.5 text-2xl font-semibold tracking-[-0.02em] tabular-nums text-ink">{value}</div>
              </div>
            ))}
          </div>
        ) : null}

        {canViewMemberCosts ? (
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-ink-dim">
            <span>{usageWindowDescription(usagePeriod)}</span>
            {totalCharge > 0 ? <span>Attributed customer charge {dollars(totalCharge)}</span> : null}
          </div>
        ) : null}

        {!canInvite && !canManage ? (
          <p className="mt-3 text-xs text-ink-dim">You can view this workspace. A company owner manages invitations and roles.</p>
        ) : null}
      </section>

      {/* Invite form (inline) */}
      {inviteOpen && canInvite && (
        <div className="bg-canvas border border-line-soft rounded-card p-4 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-[2fr_2fr_1fr_auto] gap-3 items-end">
            <div>
              <label className="block text-[11px] font-medium text-ink-dim uppercase tracking-wider mb-1">Email</label>
              <input
                type="email"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                placeholder="person@company.com"
                className="w-full px-3 py-2 bg-white border border-line rounded-lg text-sm focus:outline-none focus:border-ink"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-ink-dim uppercase tracking-wider mb-1">Name (optional)</label>
              <input
                type="text"
                value={inviteName}
                onChange={e => setInviteName(e.target.value)}
                placeholder="Jane Doe"
                className="w-full px-3 py-2 bg-white border border-line rounded-lg text-sm focus:outline-none focus:border-ink"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-ink-dim uppercase tracking-wider mb-1">Role</label>
              <select
                value={inviteRole}
                onChange={e => setInviteRole(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-line rounded-lg text-sm focus:outline-none focus:border-ink"
              >
                {roleOptions.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            {departments.length > 0 && (
              <div>
                <label className="block text-[11px] font-medium text-ink-dim uppercase tracking-wider mb-1">Department (optional)</label>
                <select
                  value={inviteDept}
                  onChange={e => setInviteDept(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-line rounded-lg text-sm focus:outline-none focus:border-ink"
                >
                  <option value="">No department</option>
                  {departments.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
            )}
            {/* Job title is a first-class HR-intake field (ROL-3) — always
                offered; it seeds the person's brief so dive queues and skill
                prefills start from their real job. Level stays behind the
                invite-context flag (it's an onboarding prefill). */}
            <div className="md:col-span-4 grid grid-cols-1 md:grid-cols-2 gap-3 pt-1 border-t border-line-soft mt-1">
              <div>
                <label className="block text-[11px] font-medium text-ink-dim uppercase tracking-wider mb-1">Job title (optional)</label>
                <input
                  type="text"
                  value={inviteTitle}
                  onChange={e => setInviteTitle(e.target.value)}
                  placeholder="e.g. Account Executive"
                  className="w-full px-3 py-2 bg-white border border-line rounded-lg text-sm focus:outline-none focus:border-ink"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[11px] font-medium text-ink-dim uppercase tracking-wider mb-1">Job description (optional — from HR)</label>
                <textarea
                  value={inviteJobDesc}
                  onChange={e => setInviteJobDesc(e.target.value)}
                  rows={2}
                  placeholder="What this role covers — responsibilities, who they work with, what they produce."
                  className="w-full px-3 py-2 bg-white border border-line rounded-lg text-sm focus:outline-none focus:border-ink"
                />
              </div>
              {featureInviteContext && (
                <div>
                  <label className="block text-[11px] font-medium text-ink-dim uppercase tracking-wider mb-1">Level (optional context)</label>
                  <select
                    value={inviteLevel}
                    onChange={e => setInviteLevel(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-line rounded-lg text-sm focus:outline-none focus:border-ink"
                  >
                    <option value="">Not set</option>
                    <option value="IC">Individual contributor</option>
                    <option value="Manager">Manager</option>
                    <option value="Exec">Exec</option>
                    <option value="Founder">Founder</option>
                  </select>
                </div>
              )}
              {featureInviteContext && (
                <p className="md:col-span-2 text-[11px] text-ink-soft">Orbit will confirm these with the person during onboarding, so they start ahead instead of from a blank slate.</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setInviteOpen(false); setInviteEmail(''); setInviteName(''); setInviteRole('AI_PARTICIPANT'); setInviteDept(''); setInviteLevel(''); setInviteTitle(''); setInviteJobDesc(''); }}
                disabled={inviteBusy}
                className="px-3 py-2 text-sm text-ink-muted hover:text-ink"
              >
                Cancel
              </button>
              <button
                onClick={handleInvite}
                disabled={inviteBusy || !inviteEmail.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                {inviteBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Invite
              </button>
            </div>
          </div>
          {paywall && (
            <PaywallModal
              payload={paywall}
              onTopUpSuccess={handlePaywallTopUpSuccess}
              onDismiss={() => setPaywall(null)}
            />
          )}
        </div>
      )}

      <section className="mt-6" aria-labelledby="company-people-heading">
        <div className="mb-3 flex items-end justify-between gap-4">
          <h2 id="company-people-heading" className="m-0 text-base font-semibold text-ink">Your people</h2>
          <span className="text-xs tabular-nums text-ink-dim">{companyMembers.length} {companyMembers.length === 1 ? 'person' : 'people'}</span>
        </div>

        <div className="overflow-hidden rounded-panel border border-line bg-white shadow-[0_1px_2px_rgba(24,25,29,0.04)]">
          <div className={`hidden ${usageGrid} items-center gap-4 border-b border-line bg-canvas px-5 py-2.5 md:grid`}>
            <span className={headCell}>Person</span>
            <span className={headCell}>Role</span>
            <span className={headCell}>Department</span>
            {showBuilder ? <span className={headCell}>Builder access</span> : null}
            {canViewMemberCosts ? <span className={headCell}>Usage · {usagePeriodLabel[usagePeriod].toLowerCase()}</span> : null}
            {canViewMemberCosts ? <span className={`${headCell} text-right`}>Cost</span> : null}
            <span className={`${headCell} sr-only`}>Actions</span>
          </div>

          {companyMembers.map(member => {
            const isFounder = Boolean(member.is_founder);
            const canManageThis = !isFounder && (member.can_manage_member ?? canManage);
            const uiRole = String(member.role || 'AI_PARTICIPANT').toUpperCase();
            const meta = roleMeta(uiRole);
            const RoleIcon = meta.icon;
            const usageKey = (member.email || '').toLowerCase();
            const usage = usageByEmail[usageKey];
            const tokens = Number(usage?.total_tokens || 0);
            const cost = incurredCost(usage);
            const share = maxMemberTokens > 0 ? tokens / maxMemberTokens : 0;
            const mutating = mutatingEmail === member.email;
            const expanded = openUsage === usageKey;
            const label = member.name || member.email;
            const toggleUsage = () => setOpenUsage(expanded ? null : usageKey);
            return (
              <React.Fragment key={member.id || member.email}>
                <div className={`grid grid-cols-1 items-center gap-3 border-b border-line-soft px-5 py-3.5 transition-colors last:border-b-0 hover:bg-canvas/60 md:gap-4 ${usageGrid} ${expanded ? 'bg-canvas/60' : ''}`}>
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-canvas-inset text-[13px] font-semibold text-ink ring-1 ring-line">
                      {label.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="flex min-w-0 items-center gap-1.5">
                        <p className="m-0 truncate text-[13.5px] font-semibold text-ink" title={label}>{label}</p>
                        {isFounder ? <span className="shrink-0 rounded-full bg-canvas-inset px-1.5 py-px text-[10px] font-semibold text-ink-muted ring-1 ring-line">Founder</span> : null}
                        {member.invite_pending ? <span className="shrink-0 rounded-full bg-[rgba(255,234,0,0.2)] px-1.5 py-px text-[10px] font-semibold text-[#8A6D00]">Invite pending</span> : null}
                      </div>
                      {member.name && member.name !== member.email ? <p className="m-0 mt-0.5 truncate text-xs text-ink-dim" title={member.email}>{member.email}</p> : null}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className={`${mobileLabel} md:hidden`}>Role</span>
                    {canManageThis ? (
                      <span className="relative flex w-full min-w-0 max-w-[11rem]">
                        <select
                          aria-label={`Role for ${label}`}
                          value={uiRole}
                          disabled={mutating}
                          onChange={event => handleRoleChange(member.email, event.target.value)}
                          className="h-8 w-full min-w-0 appearance-none truncate rounded-full border border-line bg-white bg-none py-0 pl-3 pr-7 text-[12.5px] shadow-none transition-colors hover:border-line-strong focus:border-ink-soft focus:outline-none focus:ring-0 focus-visible:ring-2 focus-visible:ring-brand-500/30 disabled:opacity-50 font-medium text-ink"
                        >
                          {roleOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-muted" />
                      </span>
                    ) : (
                      <span className={`inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-[12.5px] font-medium ring-1 ring-inset ${meta.className}`}>
                        <RoleIcon className="h-3.5 w-3.5" />
                        {roleLabel(uiRole)}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    <span className={`${mobileLabel} md:hidden`}>Department</span>
                    {canManageThis && departments.length ? (
                      <span className="relative flex w-full min-w-0 max-w-[11rem]">
                        <select
                          aria-label={`Department for ${label}`}
                          value={member.department_id || ''}
                          disabled={mutating}
                          onChange={event => handleDeptChange(member.email, event.target.value)}
                          className="h-8 w-full min-w-0 appearance-none truncate rounded-full border border-line bg-white bg-none py-0 pl-3 pr-7 text-[12.5px] shadow-none transition-colors hover:border-line-strong focus:border-ink-soft focus:outline-none focus:ring-0 focus-visible:ring-2 focus-visible:ring-brand-500/30 disabled:opacity-50 text-ink-muted"
                        >
                          <option value="">No department</option>
                          {departments.map(department => <option key={department.id} value={department.id}>{department.name}</option>)}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-muted" />
                      </span>
                    ) : null}
                    {!(canManageThis && departments.length) && member.department ? <span className="text-[13px] text-ink">{member.department}</span> : null}
                    {!(canManageThis && departments.length) && !member.department ? <span className="text-[13px] text-ink-soft">None yet</span> : null}
                  </div>

                  {showBuilder ? (
                    <div className="flex items-center gap-1.5">
                      <span className={`${mobileLabel} md:hidden`}>Builder</span>
                      {uiRole === 'OWNER' && member.is_builder_admin ? <AdminBadge /> : null}
                      {canSetExpert && (
                        <AccessToggle label="Expert" checked={Boolean(member.is_expert)} disabled={mutating} onChange={value => handleExpertToggle(member.email, value)} />
                      )}
                      {!canSetExpert && (
                        <Tooltip content="Only the Owner can choose who is an expert">
                          <span><AccessToggle label="Expert" checked={Boolean(member.is_expert)} disabled onChange={() => undefined} /></span>
                        </Tooltip>
                      )}
                    </div>
                  ) : null}

                  {canViewMemberCosts ? (
                    <button
                      type="button"
                      aria-expanded={expanded}
                      aria-controls={`usage-${member.id || usageKey}`}
                      onClick={toggleUsage}
                      className="group flex items-center gap-3 rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                    >
                      <span className={`${mobileLabel} md:hidden`}>Usage</span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-baseline justify-between gap-2">
                          <span className={`text-[13px] font-semibold tabular-nums ${tokens > 0 ? 'text-ink' : 'text-ink-soft'}`}>{usageLoading ? '…' : compactNumber(tokens)}</span>
                          <span className="text-[11px] tabular-nums text-ink-soft">{usageLoading ? '' : `${usage?.calls || 0} ${usage?.calls === 1 ? 'call' : 'calls'}`}</span>
                        </span>
                        <span className="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-canvas-inset">
                          <span className="block h-full rounded-full bg-ink-soft transition-[width] duration-700 ease-out" style={{ width: `${usageLoading ? 0 : Math.max(share * 100, tokens > 0 ? 4 : 0)}%` }} />
                        </span>
                      </span>
                      <ChevronDown className={`hidden h-3.5 w-3.5 shrink-0 text-ink-soft transition-transform group-hover:text-ink md:block ${expanded ? 'rotate-180' : ''}`} />
                    </button>
                  ) : null}
                  {canViewMemberCosts ? (
                    <button
                      type="button"
                      aria-expanded={expanded}
                      aria-controls={`usage-${member.id || usageKey}`}
                      onClick={toggleUsage}
                      className="flex items-center justify-between gap-3 rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 md:justify-end md:text-right"
                    >
                      <span className={`${mobileLabel} md:hidden`}>Cost</span>
                      <span className={`text-[13px] font-semibold tabular-nums ${cost > 0 ? 'text-ink' : 'text-ink-soft'}`}>{usageLoading ? '…' : dollars(cost)}</span>
                    </button>
                  ) : null}

                  <div className="flex items-center justify-end">
                    {canManageThis ? (
                      <Tooltip content={`Remove ${label}`}>
                        <button
                          type="button"
                          aria-label={`Remove ${label}`}
                          onClick={() => handleRemove(member.email)}
                          disabled={mutating}
                          className="grid h-8 w-8 place-items-center rounded-full text-ink-soft transition-colors hover:bg-danger-soft hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger disabled:opacity-50"
                        >
                          {mutating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserX className="h-3.5 w-3.5" />}
                        </button>
                      </Tooltip>
                    ) : (
                      <Tooltip content="The founder cannot be removed or changed">
                        <span className="grid h-8 w-8 place-items-center rounded-full text-ink-soft" aria-label="Protected">
                          <Lock className="h-3.5 w-3.5" />
                        </span>
                      </Tooltip>
                    )}
                  </div>
                </div>

                {canViewMemberCosts && expanded ? (
                  <UsageDetail id={`usage-${member.id || usageKey}`} usage={usage} compactNumber={compactNumber} dollars={dollars} />
                ) : null}
              </React.Fragment>
            );
          })}

          {!companyMembers.length ? (
            <div className="px-5 py-10 text-center">
              <p className="m-0 text-sm font-medium text-ink">No company people yet</p>
              <p className="m-0 mt-1 text-xs text-ink-dim">Invite the first person when the team is ready.</p>
            </div>
          ) : null}
        </div>
      </section>

      {servicePeople.length ? (
        <section className="mt-7" aria-labelledby="solution-partner-heading">
          <div className="mb-3">
            <h2 id="solution-partner-heading" className="m-0 text-base font-semibold text-ink">Solution partner</h2>
            <p className="mt-1 text-xs leading-5 text-ink-dim">The partner supporting this company through Agents Anywhere.</p>
          </div>

          <div className="space-y-3">
          {servicePeople.map(person => {
            return (
              <div key={person.id || person.email} className="rounded-panel border border-line bg-white px-5 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-tile bg-card text-ink ring-1 ring-line">
                      <UserCog className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="m-0 truncate text-sm font-semibold text-ink">{person.name || person.email}</p>
                      <p className="m-0 mt-0.5 text-xs text-ink-dim">Solution Partner{person.email ? ` · ${person.email}` : ''}</p>
                    </div>
                  </div>
                  <Tooltip content={consultantTooltip}><span className="rounded-full bg-card px-2.5 py-1 text-[11px] font-medium text-ink ring-1 ring-line">On your account</span></Tooltip>
                </div>
              </div>
            );
          })}
          </div>
        </section>
      ) : null}

      {canViewMemberCosts && usageByEmail.__company_automation__ ? (
        <section className="mt-7 rounded-panel border border-line bg-canvas px-5 py-4" aria-labelledby="automation-heading">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="grid h-9 w-9 place-items-center rounded-tile bg-white text-ink ring-1 ring-line"><Bot className="h-4 w-4" /></div>
              <div><h2 id="automation-heading" className="m-0 text-sm font-semibold text-ink">Company automation</h2><p className="m-0 mt-0.5 text-xs text-ink-dim">Scheduled and shared work without a person attribution.</p></div>
            </div>
            <div className="flex gap-6 text-right">
              <div><div className="text-sm font-semibold tabular-nums text-ink">{compactNumber(usageByEmail.__company_automation__.total_tokens)}</div><div className="text-[10.5px] text-ink-soft">tokens</div></div>
              <div><div className="text-sm font-semibold tabular-nums text-ink">{dollars(incurredCost(usageByEmail.__company_automation__))}</div><div className="text-[10.5px] text-ink-soft">provider cost</div></div>
            </div>
          </div>
        </section>
      ) : null}

      {canViewMemberCosts && unattributedUsage.length ? (
        <p className="mt-3 text-xs text-ink-dim">Some delivery usage could not be matched to a current person. It remains attributed to this workspace and is available for administrator reconciliation.</p>
      ) : null}
      {canViewMemberCosts && !usageComplete ? (
        <p className="mt-3 text-xs text-ink-dim" role="status">Usage is temporarily incomplete. The provider roster remains available.</p>
      ) : null}

      {/* Footnote and role catalog hidden for now.
      <p className="mt-5 text-xs leading-5 text-ink-soft">
        {isProvider
          ? 'Provider cost includes usage attributed to people on this provider roster, including their work in client workspaces.'
          : 'Company member changes sync to Builder. Your Solution Partner remains part of their provider company and does not consume a company seat.'}
      </p>

      <RolesCatalogView />
      */}
    </div>
  );
};

const UsageDetail: React.FC<{
  id: string;
  usage: any;
  compactNumber: (value: number) => string;
  dollars: (value: number) => string;
}> = ({ id, usage, compactNumber, dollars }) => (
  <div id={id} className="border-t border-line-soft bg-canvas px-5 py-4">
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <div className="rounded-card bg-white px-3.5 py-3 ring-1 ring-line">
        <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-ink-soft">Tokens</div>
        <div className="mt-1 text-sm font-semibold tabular-nums text-ink">{compactNumber(usage?.total_tokens || 0)}</div>
      </div>
      <div className="rounded-card bg-white px-3.5 py-3 ring-1 ring-line">
        <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-ink-soft">Model calls</div>
        <div className="mt-1 text-sm font-semibold tabular-nums text-ink">{usage?.calls || 0}</div>
      </div>
      <div className="rounded-card bg-white px-3.5 py-3 ring-1 ring-line">
        <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-ink-soft">Provider cost</div>
        <div className="mt-1 text-sm font-semibold tabular-nums text-ink">{dollars(usage?.incurred_cost_usd || 0)}</div>
      </div>
      <div className="rounded-card bg-white px-3.5 py-3 ring-1 ring-line">
        <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-ink-soft">Customer charge</div>
        <div className="mt-1 text-sm font-semibold tabular-nums text-ink">{dollars(usage?.customer_charge_usd || 0)}</div>
      </div>
    </div>
    <div className="mt-3 divide-y divide-line-soft overflow-hidden rounded-card border border-line bg-white">
      {(usage?.breakdown || []).map((part: any) => (
        <div key={part.surface} className="flex flex-col gap-1 px-3.5 py-2.5 text-xs sm:flex-row sm:items-center sm:justify-between">
          <span className="font-medium capitalize text-ink">{part.surface.replace(/[-_]/g, ' ')}</span>
          <span className="tabular-nums text-ink-dim">{compactNumber(part.total_tokens)} tokens · {part.calls} calls · {dollars(part.incurred_cost_usd)} provider cost{part.customer_charge_usd > 0 ? ` · ${dollars(part.customer_charge_usd)} charged` : ''}</span>
        </div>
      ))}
      {!usage?.breakdown?.length ? <div className="px-3.5 py-3 text-xs text-ink-dim">No recorded usage in this period.</div> : null}
    </div>
    <p className="m-0 mt-2 text-[11px] leading-4 text-ink-soft">Provider cost is the metered AI cost attributed to this person. Customer charge is shown separately when account pricing applies.</p>
  </div>
);

/** READ-ONLY role catalog (One Identity P5): the owner sees what each role
 *  grants BEFORE assigning it. Definitions are authored by the platform in
 *  Role Management; this view never edits. */
const RolesCatalogView: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<Array<{ value: string; label: string; rank: number; permissions: string[] }> | null>(null);

  useEffect(() => {
    if (!open || rows) return;
    (async () => {
      try {
        const axios = (await import('axios')).default;
        const { getApiBaseUrl } = await import('../../services/api');
        const r = await axios.get(`${getApiBaseUrl()}/roles/catalog-view`);
        setRows(Array.isArray(r.data) ? r.data : []);
      } catch { setRows([]); }
    })();
  }, [open, rows]);

  return (
    <div className="mt-6 rounded-panel border border-line bg-white">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-5 py-3 text-left"
      >
        <span className="text-sm font-medium text-ink">What each role can do</span>
        <span className="text-xs text-ink-soft">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <div className="border-t border-line-soft px-5 py-4 space-y-3">
          {rows === null && <div className="text-xs text-ink-soft">Loading…</div>}
          {rows?.map(role => (
            <div key={role.value} className="flex flex-wrap items-baseline gap-2">
              <span className="text-[13px] font-medium text-ink w-40 shrink-0">{role.label}</span>
              {role.permissions.length ? (
                <span className="flex flex-wrap gap-1.5">
                  {role.permissions.map(perm => (
                    <span key={perm} className="rounded-full bg-canvas px-2 py-0.5 text-[11px] text-ink-muted ring-1 ring-line">{perm}</span>
                  ))}
                </span>
              ) : (
                <span className="text-[11.5px] text-ink-soft">Standard workspace access for their rank.</span>
              )}
            </div>
          ))}
          {rows?.length === 0 && <div className="text-xs text-ink-soft">No roles published yet.</div>}
        </div>
      )}
    </div>
  );
};

export default ClientTeamTab;
